import { FC, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  Cluster,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Spinner,
  TextArea,
  Typography,
} from '@forto-dev/phoenix-ui-core'

import { useAuthorizeBookingMutation, useRejectBookingMutation } from '@/features/bookings'
import { useLocalisation } from '@/utils/localisation'

import { ActionDialogProps } from './actionDialogNew'

export const AuthorizeBookingDialog: FC<ActionDialogProps> = ({ action, setOpen }) => {
  const { translate } = useLocalisation()
  const [text, setText] = useState('')
  const [commentError, setCommentError] = useState(false)
  const queryClient = useQueryClient()
  const [processing, setProcessing] = useState<'authorize' | 'reject' | null>()

  const closeDialog = () => {
    setText('')
    setOpen(null)
  }

  const onSuccess = async () => {
    await new Promise((r) => setTimeout(r, 2000))

    await queryClient.invalidateQueries({
      queryKey: ['required-actions'],
    })
    setProcessing(null)
    closeDialog()
  }
  const authorizeBooking = useAuthorizeBookingMutation(onSuccess, closeDialog)
  const rejectBooking = useRejectBookingMutation(onSuccess, closeDialog)

  const onAuthorizeHandler = async () => {
    if (action?.entityId) {
      setProcessing('authorize')
      await authorizeBooking.mutateAsync({ id: action?.entityId, comment: text })
    }
  }
  const onRejectHandler = async () => {
    if (action?.entityId) {
      if (!text) {
        setCommentError(true)
        return
      }
      setProcessing('reject')
      await rejectBooking.mutateAsync({ id: action?.entityId, comment: text })
    }
  }

  return (
    <Dialog
      open={action != null}
      onOpenChange={(open) => {
        if (!open) {
          closeDialog()
        }
      }}
    >
      <DialogContent>
        <DialogTitle>{translate('required-actions.action.authorizeBooking')}</DialogTitle>
        <Typography variant="body">{translate('booking.overview.auth.dialog.textarea.title')}</Typography>
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={translate('booking.overview.auth.dialog.textarea.placeholder')}
          label={translate('booking.overview.auth.dialog.textarea.label_required')}
          state={commentError ? 'negative' : undefined}
          hint={commentError ? translate('required-action.action.dialog.rejectBookingCommentError') : ''}
        />

        <DialogFooter>
          <Button onClick={onRejectHandler} variant="negative" disabled={!!processing}>
            <Cluster gap={8} y="center">
              {processing === 'reject' && <Spinner size="xs" />}
              {translate('required-action.action.dialog.rejectBooking')}
            </Cluster>
          </Button>
          <Button onClick={onAuthorizeHandler} variant="primary" disabled={!!processing}>
            <Cluster gap={8} y="center">
              {processing === 'authorize' && <Spinner size="xs" />}
              {translate('required-action.action.dialog.authorizeBooking')}
            </Cluster>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
